const SITE_URL = 'https://dentalspaces.com';

// Organization schema for site-wide use
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'DentalSpaces',
  url: SITE_URL,
  logo: `${SITE_URL}/logo512.png`,
  description: 'The premier marketplace for buying and selling dental practices.',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'hello@dentalspaces.com',
    contactType: 'customer service',
  },
  sameAs: [],
};

// Website schema with SearchAction
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'DentalSpaces',
  url: SITE_URL,
  description: 'The premier marketplace for buying and selling dental practices.',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/practices-for-sale?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

// Create LocalBusiness + Offer schema for individual listings
export const createListingSchema = (listing) => {
  const formatPrice = (price) => {
    if (price >= 1000000) return `$${(price / 1000000).toFixed(1)}M`;
    return `$${(price / 1000).toFixed(0)}K`;
  };

  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/listing/${listing.id}`,
    name: listing.title,
    description: listing.description,
    address: {
      '@type': 'PostalAddress',
      addressLocality: listing.city,
      addressRegion: listing.state,
      addressCountry: 'US',
    },
    geo: listing.lat && listing.lng ? {
      '@type': 'GeoCoordinates',
      latitude: listing.lat,
      longitude: listing.lng,
    } : undefined,
    image: listing.image || `${SITE_URL}/og-image.png`,
    priceRange: formatPrice(listing.price),
    makesOffer: {
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Product',
        name: `${listing.type} - ${listing.city}, ${listing.state}`,
        description: listing.description,
      },
      price: listing.price,
      priceCurrency: 'USD',
      availability: listing.status === 'For Sale'
        ? 'https://schema.org/InStock'
        : 'https://schema.org/SoldOut',
    },
    additionalProperty: [
      {
        '@type': 'PropertyValue',
        name: 'Annual Collections',
        value: listing.collections,
      },
      {
        '@type': 'PropertyValue',
        name: 'Number of Operatories',
        value: listing.operatories,
      },
      {
        '@type': 'PropertyValue',
        name: 'Square Footage',
        value: listing.sqft,
      },
      {
        '@type': 'PropertyValue',
        name: 'Years Established',
        value: listing.yearsEstablished,
      },
      {
        '@type': 'PropertyValue',
        name: 'Active Patients',
        value: listing.patients,
      },
    ],
  };
};

// Create RealEstateAgent schema for broker pages
export const createBrokerSchema = (broker) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    '@id': `${SITE_URL}/broker/${broker.slug}`,
    name: broker.company,
    description: `${broker.company} - Dental practice broker specializing in dental practice sales and acquisitions.`,
    image: broker.logo?.startsWith('http') ? broker.logo : `${SITE_URL}${broker.logo}`,
    telephone: broker.phone,
    email: broker.email,
    url: broker.website ? `https://${broker.website.replace(/^https?:\/\//, '')}` : undefined,
    areaServed: {
      '@type': 'Country',
      name: 'United States',
    },
    makesOffer: {
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: 'Dental Practice Brokerage',
        description: 'Professional dental practice sales and acquisition services.',
      },
    },
  };
};

// Create BreadcrumbList schema
export const createBreadcrumbSchema = (items) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? `${SITE_URL}${item.url}` : undefined,
    })),
  };
};

// Create ItemList schema for listings pages
export const createListingsPageSchema = (listings, stateName = null) => {
  const title = stateName
    ? `Buy a Dental Practice in ${stateName}`
    : 'Buy a Dental Practice';

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: title,
    description: `Buy a dental practice${stateName ? ` in ${stateName}` : ''}. ${listings.length} dental practices for sale with verified financials on DentalSpaces.`,
    numberOfItems: listings.length,
    itemListElement: listings.slice(0, 10).map((listing, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${SITE_URL}/listing/${listing.id}`,
      name: listing.title,
    })),
  };
};
